import { useState } from 'react';
import { PageHead } from 'ui/kit/PageHead';
import { Link, useLocation } from 'react-router';

import { FadeIn } from 'ui/kit/uiMotion';

import classes from './Questions.module.scss';
import tutor from '../Teacher/components/TeacherFaq.module.scss';

interface FaqItem {
  q: string;
  a: string;
}

interface FaqGroup {
  title: string;
  items: FaqItem[];
}

const teacherFaqs: FaqItem[] = [
  {
    q: 'Как отправить профиль на проверку?',
    a: 'Заполните анкету и проверьте поля в профиле. Затем откройте шаг отправки на проверку. Статус и комментарии будут видны в кабинете.',
  },
  {
    q: 'Какие фото нужны для проверки паспорта?',
    a: 'Откройте проверку документов в профиле. Подготовьте разворот паспорта, страницу регистрации и селфи по подсказкам формы.',
  },
  {
    q: 'Когда открываются поиск и чаты?',
    a: 'Поиск заявок и чаты доступны после одобрения профиля. До этого продолжите заполнение анкеты или откройте комментарии к проверке.',
  },
  {
    q: 'Где посмотреть лимит откликов?',
    a: 'Лимит откликов виден в разделе Рейтинг, если сервис его отдает. Отдельного тарифа в кабинете нет.',
  },
  {
    q: 'Кто задает цену и условия пробного занятия?',
    a: 'Репетитор указывает свои ставки и готовность к пробному занятию в анкете. Это не цены сервиса. Условия занятия обсудите с учеником.',
  },
  {
    q: 'Как открыть кошелек или обратиться в поддержку?',
    a: 'Кошелек виден только при включенной функции аккаунта. По вопросам сервиса откройте Тех.поддержку и опишите ситуацию.',
  },
];

const studentGroups: FaqGroup[] = [
  {
    title: 'Задача и отклики',
    items: [
      {
        q: 'Как создать задачу и какой минимальный бюджет?',
        a: 'В Мои задачи выбери Создать задачу. Укажи класс, цель, бюджет от 500 рублей в час и удобное время. Бюджет - пожелание: итоговую цену и длительность занятия согласуй с репетитором.',
      },
      {
        q: 'Когда появятся отклики?',
        a: 'Когда задача видна репетиторам, они могут откликнуться. Отклики появятся в задаче. Срок не фиксирован: зависит от предмета, времени и цены.',
      },
    ],
  },
  {
    title: 'Профиль и чаты',
    items: [
      {
        q: 'Почему чаты пока закрыты?',
        a: 'Чат открывается после отклика репетитора. Пока отклика нет, переписка недоступна. Это не оценка тебя, просто следующий шаг после отклика.',
      },
    ],
  },
  {
    title: 'Занятия',
    items: [
      {
        q: 'Как согласовать пробное занятие и стоимость?',
        a: 'В чате согласуй время, формат и цену часа. Пробный урок обычно платный по цене репетитора. Итоговую длительность тоже лучше проговорить заранее.',
      },
    ],
  },
  {
    title: 'Отзывы',
    items: [
      {
        q: 'Как оставить отзыв о занятиях?',
        a: 'Когда занятие состоялось, открой чат и оставь отзыв. Он сначала на проверке и не публикуется сразу. Результат модерации придет в уведомлениях.',
      },
    ],
  },
  {
    title: 'Данные и доступ',
    items: [
      {
        q: 'Как изменить персональные данные?',
        a: 'ФИО, пол, дату рождения, телефон и почту нельзя поменять самостоятельно. В Настройках отправь заявку с причиной. Модератор свяжется по почте аккаунта.',
      },
    ],
  },
];

export const Questions = () => {
  const location = useLocation();
  const isTeacherRoute = location.pathname.startsWith('/teacher');

  if (!isTeacherRoute) {
    return <StudentFaq />;
  }

  return <TeacherFaq />;
};

function StudentFaq() {
  const [openKey, setOpenKey] = useState('0-0');

  return (
    <FadeIn>
      <main className={classes.studentPage}>
        <PageHead crumb='Личный кабинет / Частые вопросы' title='Частые вопросы' sub='Для учеников' />
        <section className={classes.studentCard}>
          {studentGroups.map((group, groupIndex) => (
            <div key={group.title} className={classes.studentGroup}>
              <h2 className={classes.studentGroupTitle}>{group.title}</h2>
              {group.items.map((item, itemIndex) => {
                const key = `${groupIndex}-${itemIndex}`;
                const isOpen = openKey === key;
                return (
                  <div
                    key={item.q}
                    className={isOpen ? classes.studentItemOpen : classes.studentItem}
                  >
                    <button
                      type='button'
                      className={classes.studentQuestion}
                      aria-expanded={isOpen}
                      onClick={() => setOpenKey(isOpen ? '' : key)}
                    >
                      <span>{item.q}</span>
                      <span aria-hidden>{isOpen ? '-' : '+'}</span>
                    </button>
                    {isOpen ? <p className={classes.studentAnswer}>{item.a}</p> : null}
                  </div>
                );
              })}
            </div>
          ))}
          <p className={classes.studentFoot}>
            Нужен ответ по твоей ситуации? Напиши в{' '}
            <Link to='/profile/help'>Тех.поддержку</Link> из меню кабинета.
          </p>
        </section>
      </main>
    </FadeIn>
  );
}

/* Figma 20:4191: у репетитора тот же аккордеон, что и у ученика (закрытый «+», открытый «-»). */
function TeacherFaq() {
  const [openIndex, setOpenIndex] = useState(0);

  return (
    <main className={tutor.page}>
      <PageHead crumb={<>Личный кабинет / Частые вопросы</>} title={<>Частые вопросы</>} />
      <section className={`${tutor.card} ${classes.tutorFaqList}`}>
        {teacherFaqs.map((item, index) => {
          const isOpen = openIndex === index;
          return (
            <div
              key={item.q}
              className={isOpen ? classes.studentItemOpen : classes.studentItem}
            >
              <button
                type='button'
                className={classes.studentQuestion}
                aria-expanded={isOpen}
                onClick={() => setOpenIndex(isOpen ? -1 : index)}
              >
                <span>{item.q}</span>
                <span aria-hidden>{isOpen ? '-' : '+'}</span>
              </button>
              {isOpen ? <p className={classes.studentAnswer}>{item.a}</p> : null}
            </div>
          );
        })}
      </section>
    </main>
  );
}
